import { resolveMemberMediaUrl } from '@formosa/shared/utils/mediaUrl';
import { resolveReadMedia } from './socialPostMediaModel';
import { travelMemberProductDisplayName } from '../member-identity/memberIdentityPresentation';
import { normalizeCursorDateTime } from '../pagination/cursorDateTime';
import {
  SOCIAL_COMPOSE_REPLY_POLICY_LABELS,
  SOCIAL_COMPOSE_QUOTE_POLICY_LABELS,
  SOCIAL_COMPOSE_VISIBILITIES,
  SOCIAL_COMPOSE_VISIBILITY_LABELS,
  normalizeQuotePolicy,
  normalizeReplyPolicy,
} from '../social-compose/socialComposeModel';

export const SOCIAL_POST_VISIBILITIES = SOCIAL_COMPOSE_VISIBILITIES;
export const SOCIAL_POST_VISIBILITY_LABELS = SOCIAL_COMPOSE_VISIBILITY_LABELS;

const count = (value) => Math.max(0, Number(value) || 0);

export function normalizeSocialPostDetail(detail) {
  const author = detail?.author || {};
  const linkedJourney = detail?.linkedJourneyPublication;
  const journey = linkedJourney?.available ? linkedJourney : null;
  // 行程附件已下架：保留標題供 UI 顯示 unavailable 狀態，不另打 API。
  const journeyUnavailable = linkedJourney && linkedJourney.available === false
    ? {
      title: linkedJourney.title || '',
      publicationId: linkedJourney.publicationId || null,
    }
    : null;
  const location = detail?.linkedLocation?.available ? detail.linkedLocation : null;
  const quote = detail?.quotedPost;
  const replyPolicy = normalizeReplyPolicy(detail?.replyPolicy);
  const quotePolicy = normalizeQuotePolicy(detail?.quotePolicy);
  const status = detail?.status ? String(detail.status).toUpperCase() : 'PUBLISHED';
  const visibility = detail?.visibility ? String(detail.visibility).toUpperCase() : 'PUBLIC';
  return {
    postId: detail?.postId,
    postKind: detail?.postKind || 'MOOD',
    status,
    visibility,
    text: detail?.textContent || '',
    rowVersion: Number(detail?.rowVersion) > 0 ? Number(detail.rowVersion) : 0,
    authorMemberId: detail?.authorMemberId != null
      ? String(detail.authorMemberId)
      : (author.memberId != null ? String(author.memberId) : undefined),
    replyPolicy,
    quotePolicy,
    replyPolicyLabel: SOCIAL_COMPOSE_REPLY_POLICY_LABELS[replyPolicy],
    quotePolicyLabel: SOCIAL_COMPOSE_QUOTE_POLICY_LABELS[quotePolicy],
    media: resolveReadMedia(detail),
    coverUrl: resolveMemberMediaUrl(detail?.coverFileReference),
    publishedAt: detail?.publishedAt || null,
    editedAt: detail?.editedAt || null,
    author: {
      memberId: author.memberId || detail?.authorMemberId,
      displayName: travelMemberProductDisplayName({
        displayName: author.displayName,
        username: author.username,
        nickname: author.nickname,
      }),
      avatarUrl: resolveMemberMediaUrl(author.avatarUrl),
    },
    journey: journey ? {
      publicationId: journey.publicationId,
      title: journey.title || 'Shared journey',
      summary: journey.summary || '',
      coverUrl: resolveMemberMediaUrl(journey.coverFileReference),
    } : null,
    journeyUnavailable,
    location: location ? {
      locationId: location.locationId,
      displayName: location.displayName || 'Shared location',
      locationType: location.locationType || '',
      countryCode: location.countryCode || '',
    } : null,
    quote: quote ? {
      postId: quote.postId || detail?.quotedSocialPostId,
      available: Boolean(quote.available),
      authorName: quote.available
        ? travelMemberProductDisplayName({
          displayName: quote.author?.displayName,
          username: quote.author?.username,
          nickname: quote.author?.nickname,
        })
        : '',
      text: quote.available ? (quote.textContent || '') : '',
      coverUrl: quote.available ? resolveMemberMediaUrl(quote.coverFileReference) : '',
    } : null,
    engagement: {
      likeCount: count(detail?.likeCount), saveCount: count(detail?.saveCount),
      commentCount: count(detail?.commentCount), liked: Boolean(detail?.liked),
      saved: Boolean(detail?.saved), followingAuthor: Boolean(detail?.followingAuthor),
    },
  };
}

function asRowVersion(value) {
  if (value == null || value === '') return 0;
  const version = Number(value);
  return Number.isInteger(version) && version > 0 ? version : 0;
}

export function normalizeComment(value) {
  const status = String(value?.status || (value?.deleted ? 'DELETED' : 'ACTIVE'));
  const deleted = Boolean(value?.deleted) || status === 'DELETED';
  return {
    commentId: value?.commentId == null ? value?.commentId : String(value.commentId),
    authorMemberId: value?.authorMemberId == null ? value?.authorMemberId : String(value.authorMemberId),
    parentCommentId: value?.parentCommentId == null || value?.parentCommentId === ''
      ? null
      : String(value.parentCommentId),
    rootCommentId: value?.rootCommentId == null || value?.rootCommentId === ''
      ? null
      : String(value.rootCommentId),
    content: deleted ? '' : (value?.content || ''),
    status,
    deleted,
    edited: Boolean(value?.edited),
    rowVersion: asRowVersion(value?.rowVersion),
    createdAt: normalizeCursorDateTime(value?.createdAt),
    updatedAt: value?.updatedAt || null,
    replyToAuthorMemberId: value?.replyToAuthorMemberId == null || value?.replyToAuthorMemberId === ''
      ? null
      : String(value.replyToAuthorMemberId),
    replyToAuthorName: value?.replyToAuthorName || value?.replyToDisplayName || '',
    directReplyCount: (() => {
      const raw = value?.directReplyCount ?? value?.replyCount;
      if (raw == null || raw === '') return null;
      const count = Number(raw);
      return Number.isFinite(count) && count > 0 ? count : null;
    })(),
  };
}

function commentCursor(last) {
  if (!last) return null;
  const createdAt = normalizeCursorDateTime(last.createdAt);
  const commentId = last.commentId == null ? '' : String(last.commentId);
  if (!createdAt || !/^[1-9]\d*$/.test(commentId)) return null;
  return { cursorCreatedAt: createdAt, cursorCommentId: commentId };
}

export function normalizeCommentPage(payload) {
  const items = Array.isArray(payload?.items) ? payload.items.map(normalizeComment) : [];
  const cursor = commentCursor(items.at(-1));
  return {
    items,
    hasMore: Boolean(payload?.hasMore) && cursor != null,
    cursor,
  };
}

export function commentRequest(content) {
  const uuid = typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : null;
  return { content: content.trim(), clientRequestId: uuid || `travel-web-${Date.now()}-${Math.random().toString(36).slice(2)}` };
}

export function commentEditRequest(content, expectedRowVersion) {
  return { content: String(content || '').trim(), expectedRowVersion, confirm: true };
}

export function commentDeleteRequest(expectedRowVersion) {
  return { expectedRowVersion, confirm: true };
}

export function replaceCommentById(items, next) {
  const id = String(next?.commentId ?? '');
  if (!id) return Array.isArray(items) ? items : [];
  return (Array.isArray(items) ? items : []).map((item) => (
    String(item.commentId) === id ? next : item
  ));
}

export function appendUniqueComments(current, incoming) {
  const existing = Array.isArray(current) ? current : [];
  const seen = new Set(existing.map((item) => String(item.commentId)));
  const extra = (Array.isArray(incoming) ? incoming : []).filter((item) => {
    const id = String(item?.commentId ?? '');
    if (!id || seen.has(id)) return false;
    seen.add(id);
    return true;
  });
  return extra.length ? [...existing, ...extra] : existing;
}

export function canAuthorMutateComment(comment, currentMemberId) {
  if (!comment || comment.deleted || comment.status === 'DELETED') return false;
  if (!comment.rowVersion) return false;
  const actor = String(currentMemberId ?? '').trim();
  const author = String(comment.authorMemberId ?? '').trim();
  return Boolean(actor) && actor === author;
}

export function socialCommentErrorMessage(cause) {
  const key = String(cause?.raw?.key || cause?.key || '');
  const code = cause?.raw?.code ?? cause?.code;
  if (key === 'travel.comment.permission.denied' || code === 95403) return '沒有權限修改這則留言。';
  if (key === 'travel.comment.not.found' || code === 95401) return '找不到這則留言，請重新載入。';
  if (key === 'travel.comment.target.not.accessible' || code === 95402) return '目前無法對這則貼文留言。';
  if (key === 'travel.comment.conflict' || code === 95404) return '留言已被更新，請重新載入後再試。';
  if (key === 'travel.comment.validation.failed' || code === 95400) return '留言內容無效。';
  if (key === 'travel.comment.target.not.supported') return '留言內容無效。';
  if (key === 'travel.social.post.not.found' || code === 95702) return '貼文目前無法使用。';
  return '留言操作失敗，請稍後再試。';
}

/** 留言錯誤文案 key（與 socialCommentErrorMessage 同一判斷；呈現層翻譯）。 */
export function socialCommentErrorKey(cause) {
  const key = String(cause?.raw?.key || cause?.key || '');
  const code = cause?.raw?.code ?? cause?.code;
  if (key === 'travel.comment.permission.denied' || code === 95403) return 'travel.comments.error.forbidden';
  if (key === 'travel.comment.not.found' || code === 95401) return 'travel.comments.error.notFound';
  if (key === 'travel.comment.target.not.accessible' || code === 95402) return 'travel.comments.error.targetUnavailable';
  if (key === 'travel.comment.conflict' || code === 95404) return 'travel.comments.error.conflict';
  if (key === 'travel.comment.validation.failed' || code === 95400) return 'travel.comments.error.invalid';
  if (key === 'travel.comment.target.not.supported') return 'travel.comments.error.invalid';
  if (key === 'travel.social.post.not.found' || code === 95702) return 'travel.comments.error.postUnavailable';
  return 'travel.comments.error.generic';
}

export function isSocialPostOwner(post, currentMemberId) {
  if (!post || post.status === 'DELETED') return false;
  const actor = String(currentMemberId ?? '').trim();
  const author = String(post.author?.memberId ?? post.authorMemberId ?? '').trim();
  return Boolean(actor) && actor === author;
}

export function canChangePostVisibility(post, currentMemberId) {
  if (!isSocialPostOwner(post, currentMemberId)) return false;
  return post.status === 'DRAFT' || post.status === 'PUBLISHED' || post.status === 'UNPUBLISHED';
}

export function canUnpublishPost(post, currentMemberId) {
  if (!isSocialPostOwner(post, currentMemberId)) return false;
  return post.status === 'DRAFT' || post.status === 'PUBLISHED';
}

export function canRepublishPost(post, currentMemberId) {
  if (!isSocialPostOwner(post, currentMemberId)) return false;
  return post.status === 'UNPUBLISHED';
}

export function canDeletePost(post, currentMemberId) {
  if (!isSocialPostOwner(post, currentMemberId)) return false;
  return post.status === 'DRAFT' || post.status === 'PUBLISHED' || post.status === 'UNPUBLISHED';
}

export function changePostVisibilityRequest(visibility, expectedRowVersion) {
  return { visibility, expectedRowVersion: Number(expectedRowVersion) || 0 };
}

export function postLifecycleVersionRequest(expectedRowVersion) {
  return { expectedRowVersion: Number(expectedRowVersion) || 0 };
}

export function socialPostErrorMessage(cause) {
  const key = String(cause?.raw?.key || cause?.key || '');
  const code = cause?.raw?.code ?? cause?.code ?? cause?.httpStatus;
  if (key === 'travel.social.post.conflict' || code === 95703 || code === 409) {
    return '貼文已被更新，請重新載入後再試。';
  }
  if (key === 'travel.social.post.permission.denied' || code === 95701 || code === 403) {
    return '沒有權限修改這則貼文。';
  }
  if (key === 'travel.social.post.not.found' || code === 95702 || code === 404) {
    return '貼文目前無法使用。';
  }
  if (key === 'travel.social.post.validation.failed' || code === 95700 || code === 400) {
    return '貼文操作請求無效。';
  }
  return '操作失敗，請稍後再試。';
}
